import { useRef, type ReactNode, type CSSProperties, type KeyboardEvent, type WheelEvent } from 'react';
import { DT, DS2 } from '../../theme/designTokens';
import { useIsMobile } from '../../hooks/useBreakpoint';
import { useTranslation } from '../../i18n/react';

/**
 * Horizontal, scroll-snapping card row with NO visible scrollbar. Supports
 * touch, trackpad, mouse-wheel (vertical wheel → horizontal), and keyboard
 * (Arrow keys). A right-edge fade hints there is more content. Holds any number
 * of cards — never hardcoded to a fixed count, so the catalogs grow safely.
 */
export function CardScroller({ children, ariaLabel, style }: { children: ReactNode; ariaLabel: string; style?: CSSProperties }) {
  const ref = useRef<HTMLDivElement>(null);

  function onWheel(e: WheelEvent<HTMLDivElement>) {
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && ref.current) {
      ref.current.scrollLeft += e.deltaY;
    }
  }
  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (!ref.current) return;
    if (e.key === 'ArrowRight') { ref.current.scrollLeft += 176; e.preventDefault(); }
    if (e.key === 'ArrowLeft')  { ref.current.scrollLeft -= 176; e.preventDefault(); }
  }

  return (
    <div style={{ position: 'relative', ...style }}>
      <div
        ref={ref}
        role="listbox"
        aria-label={ariaLabel}
        tabIndex={0}
        onWheel={onWheel}
        onKeyDown={onKeyDown}
        className="lobby-card-scroller"
        style={{
          display: 'flex',
          gap: 16,
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none' as CSSProperties['msOverflowStyle'],
          paddingBottom: 4,
        }}
      >
        {children}
      </div>
      <div aria-hidden style={{
        position: 'absolute', top: 0, right: 0, bottom: 4, width: 44, pointerEvents: 'none',
        background: `linear-gradient(to right, transparent, ${DT.color.pageBg})`,
      }} />
      <style>{`.lobby-card-scroller::-webkit-scrollbar { display: none; height: 0; width: 0; }`}</style>
    </div>
  );
}

export interface Segment { value: string; label: string; disabled?: boolean }

/** Pill segmented control (Public/Private, Host Skin/My Skin). Disabled segments
 *  are shown but not selectable (e.g. "My Skin" for the host). */
export function SegmentedControl({ segments, value, onChange, ariaLabel }: {
  segments: Segment[]; value: string; onChange: (v: string) => void; ariaLabel: string;
}) {
  const isMobile = useIsMobile();
  // Figma "Host Skin / My Skin" pill group: selected = gold pill (dark text),
  // unselected = light-grey pill (dark text). Roboto, matching the design's
  // segmented look. Roles/aria are unchanged so behavior + tests hold.
  return (
    <div role="radiogroup" aria-label={ariaLabel} style={{ display: 'inline-flex', gap: isMobile ? 6 : 8, flexWrap: 'wrap' }}>
      {segments.map((s) => {
        const selected = s.value === value;
        return (
          <button
            key={s.value}
            role="radio"
            aria-checked={selected}
            aria-label={s.label}
            disabled={s.disabled}
            onClick={() => !s.disabled && onChange(s.value)}
            style={{
              padding: isMobile ? '7px 14px' : '8px 18px',
              borderRadius: DT.radius.pill,
              border: 'none',
              cursor: s.disabled ? 'not-allowed' : 'pointer',
              fontFamily: DS2.font.body,
              fontWeight: 700,
              fontSize: isMobile ? 12 : 'clamp(0.8rem, 0.85vw, 14px)',
              background: selected ? DS2.gradient.gold : 'rgba(217,217,217,0.9)',
              color: selected ? '#0B0B0B' : '#0B0B0B',
              opacity: s.disabled ? 0.4 : 1,
              transition: `all ${DT.motion.slow}ms ease`,
              whiteSpace: 'nowrap',
            }}
          >
            {s.label}
          </button>
        );
      })}
    </div>
  );
}

export interface CatalogItem { id: string; label: string; image: string; locked?: boolean }

/** A horizontal catalog of cosmetic cards (table or tile). Selected/hover/locked
 *  and read-only states are all structurally supported. */
export function CosmeticCatalog({ items, selectedId, onSelect, readOnly, ariaLabel, variant }: {
  items: CatalogItem[];
  selectedId: string;
  onSelect?: (id: string) => void;
  readOnly?: boolean;
  ariaLabel: string;
  variant: 'table' | 'tile';
}) {
  const isMobile = useIsMobile();
  const { t: tc } = useTranslation('common');
  // Figma card geometry: table skins are square tiles; tile (domino) skins are
  // tall portrait cards with the domino rotated. Dark #2C2C2C surface, radius 20,
  // gold border on the selected card, Roboto label beneath.
  const cardW    = variant === 'table' ? (isMobile ? 120 : 132) : (isMobile ? 86 : 94);
  const cardH    = variant === 'table' ? (isMobile ? 120 : 132) : (isMobile ? 136 : 148);
  return (
    <CardScroller ariaLabel={ariaLabel}>
      {items.map((item) => {
        const selected = item.id === selectedId;
        const interactive = !readOnly && !item.locked && !!onSelect;
        return (
          <button
            key={item.id}
            role="option"
            aria-selected={selected}
            aria-label={item.label}
            disabled={!interactive}
            onClick={() => interactive && onSelect!(item.id)}
            data-selected={selected ? 'true' : 'false'}
            style={{
              scrollSnapAlign: 'start',
              flex: '0 0 auto',
              width: cardW,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 12,
              padding: 0,
              background: 'transparent',
              border: 'none',
              cursor: interactive ? 'pointer' : 'default',
              opacity: item.locked ? 0.4 : 1,
            }}
          >
            <div style={{
              width: cardW,
              height: cardH,
              borderRadius: 20,
              background: '#2C2C2C',
              border: `1px solid ${selected ? DT.color.gold : 'transparent'}`,
              boxShadow: selected ? `0 0 20px ${DT.color.gold}3d` : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              transition: `all ${DT.motion.slow}ms ease`,
            }}>
              <img
                src={item.image}
                alt={item.label}
                style={{
                  width: variant === 'table' ? '86%' : '78%',
                  height: variant === 'tile' ? 'auto' : 'auto',
                  maxWidth: '92%',
                  objectFit: 'contain',
                  imageRendering: 'auto',
                  transform: variant === 'tile' ? 'rotate(-90deg)' : 'none',
                }}
              />
            </div>
            <span style={{ fontFamily: DS2.font.body, fontSize: isMobile ? 12.5 : 'clamp(0.75rem, 0.8vw, 13px)', fontWeight: selected ? 600 : 400, color: DS2.color.textBright }}>
              {item.label}{item.locked ? ` · ${tc('status.comingSoon')}` : ''}
            </span>
          </button>
        );
      })}
    </CardScroller>
  );
}
